import { Controller } from '@hotwired/stimulus'
import { trackSharedContent } from '../shared_content_tracking'

// Badge "Share your achievement": native share sheet on mobile, dropdown of
// social links on web. Shares point at the current page (the user's profile).
export default class extends Controller {
  static targets = ['menu']
  static values = { title: String, text: String, url: String, imageUrl: String, badgeKey: String }

  share (event) {
    event.preventDefault()

    // Native share only on mobile — desktop browsers (incl. Chrome/macOS)
    // implement navigator.share too, so gate on device like the state map.
    if (this.isMobile() && navigator.share) {
      this.shareNatively(event.currentTarget)
      return
    }

    const opening = this.menuTarget.classList.contains('hidden')
    this.menuTarget.classList.toggle('hidden')
    this.reserveSpaceForMenu(opening ? event.currentTarget : null)
  }

  // Attach the badge artwork as a file, not just a link: share targets that
  // don't unfurl URLs (Instagram, Photos, MMS) show no image otherwise. The
  // modal already renders this same URL as an <img>, so the fetch is normally
  // served from cache — no pre-warming needed.
  async shareNatively (button) {
    if (this.sharing) return
    this.setSharing(button, true)

    let file
    try {
      const response = await fetch(this.imageUrlValue, { mode: 'cors' })
      if (!response.ok) throw new Error(`Badge image fetch failed: ${response.status}`)
      const blob = await response.blob()
      file = new File([blob], `atlas-obscura-badge-${this.badgeKeyValue}.png`, { type: blob.type })
    } catch (error) {
      // No text-only fallback here, matching the state map: a share without the
      // artwork isn't the thing the button promises.
      console.error(error)
      this.setSharing(button, false)
      return
    }

    const data = { title: this.titleValue, text: this.textValue, url: this.urlValue }
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      data.files = [file]
    }

    try {
      await navigator.share(data)
      this.trackShare('native')
    } catch (error) {
      // Dismissing the share sheet rejects with AbortError — not worth logging.
      if (error.name !== 'AbortError') console.error(error)
    } finally {
      this.setSharing(button, false)
    }
  }

  setSharing (button, sharing) {
    this.sharing = sharing
    button.disabled = sharing
    button.setAttribute('aria-busy', sharing ? 'true' : 'false')
  }

  // The dropdown overlays whatever's below it (next-badge box, CTA, footer)
  // rather than pushing it down — cheaper than growing enough to fit both
  // the dropdown *and* a displaced copy of that content. But the modal still
  // needs to be tall enough that the dropdown itself isn't clipped by the
  // dialog's own overflow:auto body, so reserve exactly that much: set (or
  // clear) an explicit min-height on the modal's content wrapper, computed
  // from the button and menu's actual rendered position/size.
  reserveSpaceForMenu (button) {
    const content = this.element.parentElement

    if (!button) {
      content.style.minHeight = ''
      return
    }

    const contentTop = content.getBoundingClientRect().top
    const menuBottom = button.getBoundingClientRect().bottom + 8 + this.menuTarget.offsetHeight
    content.style.minHeight = `${menuBottom - contentTop}px`
  }

  isMobile () {
    return (
      (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ||
      /Android|webOS|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (window.innerWidth && window.innerWidth < 768)
    )
  }

  copyLink (event) {
    event.preventDefault()
    if (navigator.clipboard) {
      navigator.clipboard.writeText(this.urlValue).catch(() => {})
    }
    this.trackShare('copy_link')
  }

  track (event) {
    this.trackShare(event.currentTarget.dataset.shareMethod)
  }

  // Klaviyo alongside PostHog: marketing flows off badge shares live there.
  // "Shared Badge" follows the "<Verb-ed> <Noun>" naming the other events use
  // (Viewed Article, Viewed Place); `method` mirrors PostHog's share_method so
  // the two systems reconcile.
  trackShare (method) {
    trackSharedContent(method)

    if (typeof window.klaviyo !== 'undefined') {
      window.klaviyo.push(['track', 'Shared Badge', { badge: this.badgeKeyValue, method }])
    }
  }
}
